// Purchases > Expense Doc data sources, one per jurisdiction, each normalised
// into the same channel-neutral shape so the list and detail pages render like
// Sales > Billing Doc:
//   MY — MyInvois AP sync (/ap-documents). Every MY e-invoice, Peppol-delivered
//        or not, is LHDN-validated, so this is the complete received set.
//   SG — InvoiceNow inbound (/peppol-ap-documents).
// Channel detail and actions (reject / respond) stay under e-Invoice Exchange;
// `channelUrl` links there.

export const TYPE_BY_ROUTE = {
  invoice: { my: 'Invoice', sg: 'Invoice' },
  'credit-note': { my: 'CreditNote', sg: 'CreditNote' },
  'debit-note': { my: 'DebitNote' },
  'refund-note': { my: 'RefundNote' },
}

const TYPE_ROUTE = {
  Invoice: 'invoice',
  CreditNote: 'credit-note',
  DebitNote: 'debit-note',
  RefundNote: 'refund-note',
}

const TYPE_LABEL = {
  Invoice: 'Invoice',
  CreditNote: 'Credit Note',
  DebitNote: 'Debit Note',
  RefundNote: 'Refund Note',
}

const num = (v) => (v == null || isNaN(v) ? null : Number(v))

function myStatus(status) {
  return {
    label: status || '—',
    color: { Valid: 'success', Cancelled: 'warning', Rejected: 'danger' }[status] || 'primary',
  }
}

function sgStatus(d) {
  if (d.status !== 'RESPONDED') return { label: 'Awaiting response', color: 'primary' }
  if (d.responseCode === 'AP') return { label: 'Accepted', color: 'success' }
  if (d.responseCode === 'RE') return { label: 'Rejected', color: 'danger' }
  return { label: 'Responded', color: 'warning' }
}

export const SOURCES = {
  my: {
    listAction: 'apDocuments/fetchApDocuments',
    detailAction: 'apDocuments/fetchApDocument',
    getter: 'apDocuments/documents',
    state: 'apDocuments',
    typeParam: 'typeName',
    sortFields: { docNo: 'internalId', supplier: 'supplierName', issueDate: 'dateTimeIssued', total: 'totalPayableAmount' },
    statusOptions: [
      { value: 'Valid', text: 'Valid' },
      { value: 'Rejected', text: 'Rejected' },
      { value: 'Cancelled', text: 'Cancelled' },
    ],
    toRow: (d) => ({
      id: d.uuid,
      docNo: d.internalId,
      supplierName: d.supplierName,
      supplierTaxId: d.supplierTIN,
      issueDate: d.dateTimeIssued,
      total: d.totalPayableAmount,
      currency: d.documentCurrency,
      status: myStatus(d.status),
    }),
    toDetail: (d) => {
      const lines = Array.isArray(d.lines) ? d.lines : []
      const tax = lines.reduce((sum, l) => sum + (Number(l.taxAmount) || 0), 0)
      return {
        id: d.uuid,
        docNo: d.internalId,
        type: d.typeName,
        typeLabel: TYPE_LABEL[d.typeName] || d.typeName,
        typeRoute: TYPE_ROUTE[d.typeName] || 'invoice',
        status: myStatus(d.status),
        supplierName: d.supplierName,
        supplierTaxId: d.supplierTIN,
        issueDate: d.dateTimeIssued,
        dueDate: null,
        currency: d.documentCurrency,
        discount: num(d.totalDiscount),
        subTotal: num(d.totalExcludingTax),
        tax: lines.length ? tax : null,
        total: num(d.totalPayableAmount),
        remarks: null,
        items: lines.map((l) => {
          // l.totalAmount is LHDN's ItemPriceExtension (qty x price, before discount
          // and tax), so build the line total from the amount excluding tax + tax.
          const subTotal = l.lineExtensionAmount != null ? num(l.lineExtensionAmount) : num(l.taxableAmount)
          const tax = num(l.taxAmount)
          return {
            item: l.description,
            description: '',
            quantity: num(l.quantity),
            unitPrice: num(l.unitPrice),
            tax,
            subTotal,
            total: subTotal != null ? Math.round((subTotal + (tax || 0)) * 100) / 100 : null,
          }
        }),
        channelUrl: d.uuid ? `/ap-documents/${d.uuid}` : null,
      }
    },
  },
  sg: {
    listAction: 'peppolApDocuments/fetchRecords',
    detailAction: 'peppolApDocuments/fetchRecord',
    getter: 'peppolApDocuments/documents',
    state: 'peppolApDocuments',
    typeParam: 'documentType',
    sortFields: { docNo: 'documentId', supplier: 'supplierName', issueDate: 'issueDate', total: 'payableAmount' },
    statusOptions: [
      { value: 'RECEIVED', text: 'Awaiting response' },
      { value: 'RESPONDED', text: 'Responded' },
    ],
    toRow: (d) => ({
      id: d.id,
      docNo: d.documentId,
      supplierName: d.supplierName,
      supplierTaxId: d.supplierUen,
      issueDate: d.issueDate,
      total: d.payableAmount,
      currency: d.documentCurrency,
      status: sgStatus(d),
    }),
    toDetail: (d) => ({
      id: d.id,
      docNo: d.documentId,
      type: d.documentType,
      typeLabel: TYPE_LABEL[d.documentType] || d.documentType,
      typeRoute: TYPE_ROUTE[d.documentType] || 'invoice',
      status: sgStatus(d),
      supplierName: d.supplierName,
      supplierTaxId: d.supplierUen,
      issueDate: d.issueDate,
      dueDate: d.dueDate,
      currency: d.documentCurrency,
      discount: null,
      subTotal: num(d.taxExclusiveAmount),
      tax: num(d.taxAmount),
      total: num(d.payableAmount),
      remarks: d.note,
      items: (d.lineItems || []).map((l) => {
        const subTotal = num(l.lineAmount)
        const tax =
          subTotal != null && l.taxPercent != null ? (subTotal * Number(l.taxPercent)) / 100 : null
        return {
          item: l.name || l.description,
          description: l.name ? l.description || '' : '',
          quantity: num(l.quantity),
          unitPrice: num(l.unitPrice),
          tax,
          subTotal,
          total: subTotal != null ? subTotal + (tax || 0) : null,
        }
      }),
      channelUrl: `/received-invoices/${d.id}`,
    }),
  },
}

export function jurisdictionOf(store) {
  const tenant = store.state.AppActiveTenant || store.state.tenant?.tenant?.[0]
  return tenant?.enableSgPeppol === true ? 'sg' : 'my'
}
